import React, { useState } from "react";
import { Form, Formik } from "formik";
import { FormikTextField } from "../../../Common/FormFields";
import * as Yup from "yup";
import { httpGetVendorDetailsByDuns } from "../../../../service/httpClient";
import * as RolePermissionConstants from "../../../../constants/RolePermissionConstants";
import * as Constants from "../../../../constants/Constants";
import { getUserRolePermission } from "../../../../utils/rolePermissionAccess";
import { Row, Col, Button } from "react-bootstrap";
import "./AddExistingVendor.css";
import {
  // validatedDunsConnectionFields,
  getInitialFormValues,
  getFormValidations,
  getFormFields,
  newVendorMerchantFields,
  newVendorAddressFields,
  newVendorContactFields,
  formatPhone,
} from "./formFields";
import axios from "../../../../service/axios";
import Cookies from "js-cookie";
import OnSubmitErrorMessage from "../../../Common/Errors/OnSubmitErrorMessage";

const validationRules = Yup.object().shape({
  dunsNumber: Yup.number()
    .typeError("Duns # must be 9 digit number")
    .required("Duns number is required")
    .min(9, "Duns # should be 9 digit."),
});

const getCompanyName = (existingCompanyDataList) => {
  if (existingCompanyDataList.length > 0) {
    return `${existingCompanyDataList[0].vendorCorpName}`;
  } else {
    return "Not available";
  }
};

const getDunsNumber = (existingCompanyDataList) => {
  if (existingCompanyDataList.length > 0) {
    return `${existingCompanyDataList[0].duns}`;
  } else {
    return "Not available";
  }
};

const connectionCombinationPhrase = {
  VDF_chub: "VDF/Commerce Hub",
  ["Owned Inventory_directEdi"]: "Owned Vendor",
  Both_directEdiRDS: "Big Ticket (Owned and VDF)",
  VDF_directEdi: "VDF/Direct EDI",
  ["Owned Inventory_directEdiRDS"]: "Big Ticket (Owned only)",
  Both_chub: "VDF/TV/CH",
  VDF_directEdiRDS: "Big Ticket (VDF only)",
  ["Owned Inventory_chub"]: "TV/CH",
  Both_directEdi: "Owned and VDF",
  Consignment_chub: "Consignment/Commerce Hub",
  Consignment_directEdi: "Consignment/Direct EDI",
  Consignment_directEdiRDS: "Big Ticket (Consignment only)",
  MP_mcymirakl: "Market Place/Macys-Mirakl",
  MP_blmmirakl: "Market Place/Bloomies-Mirakl",
};

const getConnections = (existingCompanyDataList) => {
  let connection = "";
  let merchandiseType = "";
  if (existingCompanyDataList.length > 0) {
    existingCompanyDataList.forEach((item) => {
      merchandiseType = item?.merchandiseType;
      connection =
        connection +
        connectionCombinationPhrase[
          `${item.merchandiseType}_${item.vendorConnectionPlatform}`
        ] +
        "; ";
    });
  }
  if (
    merchandiseType === Constants.VOB_MERCHANDISE_TYPE_MARKETPLACE &&
    connection.indexOf("undefined") > -1
  ) {
    return "Market Place";
  }
  return connection;
};

const checkIfConnectionCombinationValid = (
  vendorDetailsList,
  { merchandiseType, vendorConnectionPlatform }
) => {
  if (!vendorDetailsList) {
    return false;
  } else {
    let existingConnectionCombination = [];

    if (vendorDetailsList.length > 0) {
      vendorDetailsList.forEach((item) => {
        existingConnectionCombination.push(
          `${item.merchandiseType}_${item.vendorConnectionPlatform}`
        );
      });
    }

    if (
      existingConnectionCombination.includes(
        `${merchandiseType}_${vendorConnectionPlatform}`
      )
    ) {
      return false;
    } else {
      return true;
    }
  }
};

const secondaryVendorInitialValues = getInitialFormValues([
  ...newVendorMerchantFields,
  ...newVendorAddressFields,
  ...newVendorContactFields,
]);

const secondaryVendorValidationRules = getFormValidations([
  ...newVendorMerchantFields,
  ...newVendorAddressFields,
  ...newVendorContactFields,
]);

const getPrimaryVendorId = (vendorList) => {
  let vendorId = "";
  vendorList.forEach((vendor) => {
    if (
      vendor.isPrimaryVendor ||
      vendor.isPrimaryVendor === null ||
      vendor.isPrimaryVendor === undefined
    ) {
      vendorId = vendor.id;
    }
  });
  return vendorId;
};

const getProfileTypeFromConnection = ({
  merchandiseType,
  vendorConnectionPlatform,
}) => {
  let connection = "";
  if (merchandiseType === "VDF") {
    connection = "vdf_" + `${vendorConnectionPlatform}`;
  }
  if (merchandiseType === "Owned Inventory") {
    connection = "tv_" + `${vendorConnectionPlatform}`;
  }
  if (merchandiseType === "Both") {
    connection = "both_" + `${vendorConnectionPlatform}`;
  }
  if (merchandiseType === "Consignment") {
    connection = "consignment_" + `${vendorConnectionPlatform}`;
  }

  return connection;
};

const getCertifiedAsMinorityFromPrimaryVendor = (vendorList) => {
  let certifiedAsMinority = "";
  vendorList.forEach((vendor) => {
    if (
      vendor.isPrimaryVendor ||
      vendor.isPrimaryVendor === null ||
      vendor.isPrimaryVendor === undefined
    ) {
      certifiedAsMinority = vendor.certifiedAsMinority;
    }
  });
  return certifiedAsMinority;
};

const getIsAffidavitFromPrimaryVendor = (vendorList) => {
  let isAffidavit = false;
  vendorList.forEach((vendor) => {
    if (
      vendor.isPrimaryVendor ||
      vendor.isPrimaryVendor === null ||
      vendor.isPrimaryVendor === undefined
    ) {
      isAffidavit = vendor.isAffidavit;
    }
  });
  return isAffidavit;
};

const AddExistingVendor = () => {
  const [ifDunsValid, setIfDunsValid] = useState(false);
  const [ifDunsInvalid, setIfDunsInvalid] = useState(false);
  const [validatedDunsNo, setValidatedDunsNo] = useState(null);
  const [validatedVendorData, setValidatedVendorData] = useState(null);
  const [ifConnectionInvalid, setIfConnectionInvalid] = useState(false);
  const [validatedConnectionType, setValidatedConnectionType] = useState(null);
  const [createVendorErr, setCreateVendorErr] = useState(null);
  const [createVendorErrType, setCreateVendorErrType] = useState("");
  const [submitError, setSubmitError] = useState(false);
  const [
    merchandiseTypeAndConnection,
    setMerchandiseTypeAndConnection,
  ] = useState([]);
  const [addressError, setAddressError] = useState("");

  const resetDunsValidationStatus = (e, resetForm) => {
    e.preventDefault();
    resetForm();
    setIfDunsValid(false);
    setIfDunsInvalid(false);
    setValidatedVendorData(null);
    setValidatedDunsNo(null);
  };

  const resetConnectionSelection = (e, resetForm) => {
    e.preventDefault();
    resetForm();
    setIfConnectionInvalid(false);
    setValidatedConnectionType(null);
  };

  const resetCreateVendorSelection = (e, resetForm) => {
    e.preventDefault();
    resetForm();
    setCreateVendorErr(null);
    setCreateVendorErrType("");
  };

  const getMerchandiseTypeAndConnection = (data = []) => {
    const merchandiseTypeFlag =
      data?.length > 0 &&
      data.some((item) => item.merchandiseType === "Consignment");
    let merchandiseTypeAndConnectionArr = [];

    if (merchandiseTypeFlag) {
      merchandiseTypeAndConnectionArr = [
        {
          name: "merchandiseType",
          type: "select",
          length: 6,
          label: "Vendor Fulfillment Type",
          required: true,
          initialValue: "",
          validation: Yup.string().required(
            "The vendor fulfillment type is required."
          ),
          selectOptions: [
            {
              optionValue: "VDF",
              optionName: "Vendor Direct Fulfillment",
            },
            {
              optionValue: "Owned Inventory",
              optionName: "Traditional Vendor",
            },
          ],
          ["data-testid"]:
            "addExistingVendor_form_connection_select_merchandiseType",
        },
        {
          name: "vendorConnectionPlatform",
          type: "select",
          length: 6,
          label: "Vendor Connection",
          required: true,
          initialValue: "",
          validation: Yup.string().required(
            "The vendor connection is required."
          ),
          selectOptions: [
            {
              optionValue: "chub",
              optionName: "Commerce Hub",
            },
            {
              optionValue: "directEdi",
              optionName: "Direct EDI",
            },
            {
              optionValue: "directEdiRDS",
              optionName: "Direct EDI RDS",
            },
          ],
          ["data-testid"]:
            "addExistingVendor_form_connection_select_vendorConnectionPlatform",
        },
      ];
    } else {
      merchandiseTypeAndConnectionArr = [
        {
          name: "merchandiseType",
          type: "select",
          length: 6,
          label: "Vendor Fulfillment Type",
          required: true,
          initialValue: "",
          validation: Yup.string().required(
            "The vendor fulfillment type is required."
          ),
          selectOptions: [
            {
              optionValue: "VDF",
              optionName: "Vendor Direct Fulfillment",
            },
            {
              optionValue: "Owned Inventory",
              optionName: "Traditional Vendor",
            },
            {
              optionValue: "Consignment",
              optionName: "Consignment",
            },
          ],
          ["data-testid"]:
            "addExistingVendor_form_connection_select_merchandiseType",
        },
        {
          name: "vendorConnectionPlatform",
          type: "select",
          length: 6,
          label: "Vendor Connection",
          required: true,
          initialValue: "",
          validation: Yup.string().required(
            "The vendor connection is required."
          ),
          selectOptions: [
            {
              optionValue: "chub",
              optionName: "Commerce Hub",
            },
            {
              optionValue: "directEdi",
              optionName: "Direct EDI",
            },
            {
              optionValue: "directEdiRDS",
              optionName: "Direct EDI RDS",
            },
          ],
          ["data-testid"]:
            "addExistingVendor_form_connection_select_vendorConnectionPlatform",
        },
      ];
    }

    setMerchandiseTypeAndConnection(merchandiseTypeAndConnectionArr);
  };

  return (
    <>
      <Col sm={12} md={12} lg={12} xl={12}>
        {submitError && (
          <OnSubmitErrorMessage type="error" message={createVendorErr} />
        )}
      </Col>
      {!validatedDunsNo && (
        <Col sm={6} md={6} lg={5} xl={4}>
          <p className="addExistingVendor_pageTitle">
            Step 1 – Enter Duns # and hit "Validate"
          </p>
          <Formik
            initialValues={{
              dunsNumber: "",
            }}
            validationSchema={validationRules}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              httpGetVendorDetailsByDuns(values.dunsNumber)
                .then(({ data }) => {
                  getMerchandiseTypeAndConnection(data);
                  setValidatedVendorData(data);
                  setValidatedDunsNo(values.dunsNumber);
                  setSubmitting(false);
                  setIfDunsValid(true);
                  setIfDunsInvalid(false);
                })
                .catch((err) => {
                  console.log("ERR:", err);
                  setValidatedVendorData(null);
                  setIfDunsValid(false);
                  setIfDunsInvalid(true);
                  setSubmitting(false);
                });
            }}
            render={({ isSubmitting, isValid, dirty, resetForm }) => (
              <Form data-testid="addExistingVendor_form">
                <Row>
                  <Col xs={12} style={{ height: 110 }}>
                    <FormikTextField
                      data-testid="addExistingVendor_inputfield_duns"
                      name="dunsNumber"
                      label="Duns #"
                      required={true}
                      placeHolder="123456789"
                      isValid={ifDunsValid}
                      maxLength={9}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        style={{ marginRight: 20 }}
                        className="button-later-md"
                        disabled={isSubmitting || !dirty}
                        // type="reset"
                        onClick={(e) => resetDunsValidationStatus(e, resetForm)}
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="addExistingVendor_validateButton"
                        className="button-primary-md"
                        type="submit"
                        disabled={
                          !getUserRolePermission(
                            RolePermissionConstants.VENDORPLATFORM_MYDASHBOARD_ADDVENDOR,
                            RolePermissionConstants.VMP_ADDVENDOR
                          ) === RolePermissionConstants.EDIT ||
                          !isValid ||
                          !dirty ||
                          isSubmitting
                        }
                      >
                        Validate
                      </Button>
                    </div>
                    {ifDunsInvalid && (
                      <div className="dunsInvalidErrMsg">
                        This Duns # does not exist within VMP. Please verify the
                        Duns # or contact Mtech for support
                      </div>
                    )}
                  </Col>
                </Row>
              </Form>
            )}
          />
        </Col>
      )}
      {validatedDunsNo && !validatedConnectionType && (
        <Col sm={8} md={8} lg={7} xl={6}>
          <div className="addExistingVendor_dunsValidated_companyDetail">
            Company Name:
            <span className="dunsValidated_companyDetails">
              {getCompanyName(validatedVendorData)}
            </span>
            <br />
            Duns:
            <span className="dunsValidated_companyDetails">
              {getDunsNumber(validatedVendorData)}
            </span>
            <br />
            Existing Connection(s):
            <span className="dunsValidated_companyDetails">
              {getConnections(validatedVendorData)}
            </span>
          </div>
          <p className="addExistingVendor_pageTitle">
            Step 2 – Select Fulfillment Type/Vendor Connection and hit
            “Validate”
          </p>
          <Formik
            initialValues={getInitialFormValues(merchandiseTypeAndConnection)}
            validationSchema={getFormValidations(merchandiseTypeAndConnection)}
            onSubmit={(values, { setSubmitting }) => {
              setIfConnectionInvalid(false);
              setSubmitting(true);
              if (
                checkIfConnectionCombinationValid(validatedVendorData, values)
              ) {
                setValidatedConnectionType({ ...values });
                setSubmitting(false);
              } else {
                setIfConnectionInvalid(true);
                setSubmitting(false);
              }
            }}
            render={({
              isSubmitting,
              isValid,
              dirty,
              resetForm,
              setFieldValue,
            }) => (
              <Form
                data-testid="addExistingVendor_form_connection"
                onChange={(e) => {
                  const fieldName = e.target.name;
                  const fieldValue = e.target.value;
                  if (fieldName === "merchandiseType") {
                    setFieldValue(
                      "vendorConnectionPlatform",
                      fieldValue === "Consignment" ? "directEdi" : "",
                      true
                    );
                    let isDisableVendConPlatformField =
                      fieldValue === "Consignment" ? true : false;
                    let newMerchandiseTypeAndConnection = merchandiseTypeAndConnection.map(
                      (x) => ({
                        ...x,
                        disabled:
                          x.name === "vendorConnectionPlatform"
                            ? isDisableVendConPlatformField
                            : false,
                      })
                    );
                    setMerchandiseTypeAndConnection(
                      newMerchandiseTypeAndConnection
                    );
                  }
                }}
              >
                <Row>
                  {getFormFields(merchandiseTypeAndConnection).map(
                    (field) => field
                  )}
                </Row>
                <Row>
                  <Col xs={12}>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        style={{ marginRight: 20 }}
                        className="button-later-md"
                        disabled={isSubmitting || !dirty}
                        // type="reset"
                        onClick={(e) => resetConnectionSelection(e, resetForm)}
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="addExistingVendor_connection_validateButton"
                        className="button-primary-md"
                        type="submit"
                        disabled={!isValid || !dirty || isSubmitting}
                      >
                        Validate
                      </Button>
                    </div>
                    {ifConnectionInvalid && (
                      <div className="dunsInvalidErrMsg">
                        This Duns # already contains a vendor record with the
                        selected Vendor Fulfillment Type and Vendor Connection.
                        Please verify the Duns # or contact Mtech for support.
                      </div>
                    )}
                  </Col>
                </Row>
              </Form>
            )}
          />
        </Col>
      )}
      {validatedDunsNo && validatedVendorData && validatedConnectionType && (
        <Col sm={12} md={10} lg={10} xl={8}>
          <div className="addExistingVendor_dunsValidated_companyDetail">
            Company Name:
            <span className="dunsValidated_companyDetails">
              {getCompanyName(validatedVendorData)}
            </span>
            <br />
            Duns:
            <span className="dunsValidated_companyDetails">
              {getDunsNumber(validatedVendorData)}
            </span>
            <br />
            Existing Connection(s):
            <span className="dunsValidated_companyDetails">
              {getConnections(validatedVendorData)}
            </span>
            <br />
            New Connection:
            <span className="dunsValidated_companyDetails">
              {getConnections([{ ...validatedConnectionType }])}
            </span>
          </div>
          <p className="addExistingVendor_pageTitle">
            Step 3 – Enter remaining vendor/merchant information and hit “Add
            Vendor”
          </p>
          <Formik
            initialValues={secondaryVendorInitialValues}
            validationSchema={secondaryVendorValidationRules}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              setCreateVendorErr(null);
              setCreateVendorErrType("");
              setSubmitError(false);

              let vendorDetails = {
                vendorCorpName: validatedVendorData[0].vendorCorpName || "",
                merchantFirstName: values.merchantFirstName,
                merchantLastName: values.merchantLastName,
                merchantEmail: values.merchantEmail,
                vendorInfo: {
                  ...validatedConnectionType,
                  duns: validatedDunsNo,
                  isPrimaryVendor: false,
                  primaryVendorId: getPrimaryVendorId(validatedVendorData),
                  profileType: getProfileTypeFromConnection(
                    validatedConnectionType
                  ),
                  streetAddress: values.streetAddress,
                  suiteUnitFloor: values.suiteUnitFloor,
                  city: values.city,
                  state: values.state,
                  postalCode: values.postalCode,
                  country: values.country,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  corpEmailId: values.corpEmailId,
                  phoneNumber: values.phoneNumber,
                  extension: values.extension,
                  certifiedAsMinority: getCertifiedAsMinorityFromPrimaryVendor(
                    validatedVendorData
                  ),
                  isAffidavit: getIsAffidavitFromPrimaryVendor(
                    validatedVendorData
                  ),
                  vendorAddType:
                    Cookies.get(Constants.USER_TYPE) === Constants.INTERNAL_UI
                      ? Constants.INTERNAL_BACK_END
                      : Cookies.get(Constants.USER_TYPE) ===
                        Constants.EXTERNAL_UI
                      ? Constants.EXTERNAL_BACK_END
                      : "",
                },
              };

              axios()
                .post("/vendorProfile/addVendorByMerchant", vendorDetails)
                .then((res) => {
                  if (res.status === 201 && res.data.vendorProfileId) {
                    setCreateVendorErr(null);
                    setCreateVendorErrType("");
                    localStorage.setItem("vid", res.data.vendorProfileId);
                    window.location = `/boarding/vendor/company/${res.data.vendorProfileId}`;
                  } else {
                    let msg = res?.data
                      ? res.data?.error
                        ? res.data?.error?.message
                        : res.data?.message
                      : "Unable to create a vendor.";
                    const type = msg.includes(
                      Constants.ADD_VENDOR_SERVER_ERROR_MSG_MAP.corpEmailId
                    )
                      ? Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.corpEmailId
                      : Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.other;
                    setCreateVendorErr(msg);
                    setCreateVendorErrType(type);
                    setSubmitting(false);
                    setSubmitError(true);
                  }
                })
                .catch(({ response }) => {
                  let msg = response?.data
                    ? response.data?.error
                      ? response.data?.error?.message
                      : response.data?.message
                    : "Unable to create a vendor.";
                  const type = msg.includes(
                    Constants.ADD_VENDOR_SERVER_ERROR_MSG_MAP.corpEmailId
                  )
                    ? Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.corpEmailId
                    : Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.other;
                  setCreateVendorErr(msg);
                  setCreateVendorErrType(type);
                  setSubmitting(false);
                  setSubmitError(true);
                });
            }}
            render={({
              isSubmitting,
              isValid,
              dirty,
              resetForm,
              values,
              setFieldValue,
            }) => (
              <Form
                data-testid="addExistingVendor_form_merchantData"
                onChange={(e) => {
                  const fieldName = e.target.name;
                  const fieldValue = e.target.value;
                  if (
                    fieldName === "state" ||
                    fieldName === "postalCode" ||
                    fieldName === "country"
                  ) {
                    setFieldValue(fieldName, fieldValue.toUpperCase(), true);
                  }
                  if (fieldName === "phoneNumber") {
                    setFieldValue(fieldName, formatPhone(fieldValue), true);
                  }

                  if (
                    fieldName ===
                      Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.corpEmailId &&
                    createVendorErrType === fieldName
                  ) {
                    setCreateVendorErr(null);
                    setCreateVendorErrType("");
                  }
                  if (
                    createVendorErrType ===
                    Constants.ADD_VENDOR_SERVER_ERROR_TYPES_MAP.other
                  ) {
                    setCreateVendorErr(null);
                    setCreateVendorErrType("");
                  }
                }}
              >
                <Row>
                  {getFormFields(newVendorMerchantFields).map((field) => field)}
                </Row>
                <Row>
                  {getFormFields(
                    newVendorAddressFields,
                    setFieldValue,
                    values,
                    setAddressError,
                    addressError
                  ).map((field) => field)}
                </Row>
                <Row>
                  {getFormFields(newVendorContactFields).map((field) => field)}
                </Row>
                <Row>
                  <Col xs={12}>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        style={{ marginRight: 20 }}
                        className="button-later-md"
                        disabled={isSubmitting || !dirty}
                        // type="reset"
                        onClick={(e) =>
                          resetCreateVendorSelection(e, resetForm)
                        }
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="addExistingVendor_merchantData_addVendorButton"
                        className="button-primary-md"
                        type="submit"
                        disabled={!isValid || !dirty || isSubmitting}
                      >
                        Add Vendor
                      </Button>
                    </div>
                    {!submitError && createVendorErr && (
                      <div className="dunsInvalidErrMsg">{createVendorErr}</div>
                    )}
                  </Col>
                </Row>
              </Form>
            )}
          />
        </Col>
      )}
    </>
  );
};

export default AddExistingVendor;
